import PropTypes from 'prop-types';
import { Controller, useFormContext } from 'react-hook-form';
import { components } from 'react-select';
import Select from 'react-select/creatable';
import { forwardRef } from 'react';
import { Subtitle2Text } from '@/utils/global-fonts';
import { getNestedErrorMessage } from '@/utils/utils';
import ValidationErrorMessage from '@/components/error/validation-error-component';
import useSelectStyle from '@/hooks/use-select-style';
import Styled from './creatable-search.style';

const { Option } = components;

const CustomSelectOption = (props) => {
  const { data } = props;
  const { label, __isNew__: isNew } = data;
  if (isNew) {
    return (
      <Option {...props}>
        <Styled.StyledDiv>
          <Styled.AddIcon size="16px" fontWeight={600} />
          <Subtitle2Text>{label}</Subtitle2Text>
        </Styled.StyledDiv>
      </Option>
    );
  }
  return <Option {...props}>{label}</Option>;
};

CustomSelectOption.defaultProps = {};
CustomSelectOption.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string,
    __isNew__: PropTypes.bool,
  }).isRequired,
};

// Create select
/**
 *
 * @param {object} props - props
 * @returns {JSXElement} - SelectList
 */
const CreatableSelect = forwardRef((props, ref) => {
  const {
    header,
    name,
    required,
    customStyle,
    options,
    allowSelectAll,
    label,
    menuPlacement,
    placeholder,
    isClearable,
    isSearchable,
    isDisabled,
    isLoading,
    filterOption,
    fontSize,
    onChange,
    cy,
    onInputChange,
    closeMenuOnSelect,
    controlShouldRenderValue,
    hideSelectedOptions,
    maxMenuHeight,
    margin,
    noOptionsMessage,
    defaultMenuIsOpen,
    onCreateOption,
    formatCreateLabel,
    isMulti,
    isGrouped,
    groupedOptions,
    customGroupLabel,
    control: propControl,
    formState: propFormState,
  } = props;

  const selectStyle = useSelectStyle();

  const contextForm = useFormContext();

  // Use props if provided, otherwise fall back to context
  const control = propControl || contextForm?.control;
  const formState = propFormState || contextForm?.formState;
  const errors = formState?.errors || {};
  const formIsLoading =
    formState?.isSubmitting || formState?.isLoading || false;

  const errorMessage = getNestedErrorMessage(name, errors);

  /**
   *
   * @param {*} data - data
   * @returns {void}
   */
  const formatGroupLabel = (data) =>
    customGroupLabel ? (
      customGroupLabel(data)
    ) : (
      <Styled.GroupContainer>
        <Styled.Span>{data?.label}</Styled.Span>
      </Styled.GroupContainer>
    );

  /**
   *
   * @param {*} value - value
   * @returns {void}
   */
  const getSelectedOptionFromGroups = (value) => {
    let returnValue;
    if (isMulti) {
      returnValue = [];
    }

    groupedOptions.forEach((groupOption) => {
      if (isMulti) {
        groupOption.options.forEach((option) => {
          if (value.includes(option.value)) {
            returnValue.push(option);
          }
        });
      } else {
        const selectedItem = groupOption.options.find(
          (option) => option.value === value,
        );

        if (selectedItem) {
          returnValue = selectedItem;
        }
      }
    });

    return returnValue;
  };

  /**
   * @returns {void}
   */
  const getOptions = () => {
    if (isLoading && formIsLoading) return [];

    if (isGrouped) return groupedOptions;

    return options;
  };

  /**
   * @param {*} opts - options
   * @param {*} fieldValue - field value
   * @returns {void}
   */
  const getNestedSelect = (opts, fieldValue) => {
    let selected;
    opts.forEach((option) => {
      if (option.value === fieldValue) {
        selected = option;
      }
      if (option.options) {
        getNestedSelect(option.options);
      }
    });

    return selected;
  };

  /**
   * Creates a change handler for react-select that normalizes values between single and multi-select modes
   * @param {Function} onValueChange - React Hook Form field onChange function
   * @param {Function} onChangeCallback - Optional callback function called when selection changes
   * @param {boolean} isMultiSelect - Whether the select is in multi-select mode
   * @returns {Function} Handler function that processes selection changes
   */
  const createHandleChange =
    (onValueChange, onChangeCallback, isMultiSelect) => (selected) => {
      if (onChangeCallback) {
        onChangeCallback(selected);
      }

      if (isMultiSelect) {
        // For multi-select, return array of values
        return onValueChange(
          selected ? selected.map((option) => option.value) : [],
        );
      }
      // For single select, return single value
      return onValueChange(selected ? selected.value : selected);
    };
  /**
   * Creates a function that gets the currently selected options based on form field value
   * @param {any} fieldValue - Current value from React Hook Form field
   * @param {boolean} isMultiSelect - Whether the select is in multi-select mode
   * @param {Array} optionsList - Array of available options
   * @returns {Function} Function that returns selected option(s) in react-select format
   */
  const createGetSelected = (fieldValue, isMultiSelect, optionsList) => () => {
    if (!fieldValue) {
      return isMultiSelect ? [] : null;
    }

    if (isMultiSelect && fieldValue) {
      // For multi-select, return array of options
      if (Array.isArray(fieldValue)) {
        return fieldValue
          .map((val) => optionsList.find((option) => option.value === val))
          .filter(Boolean);
      }
      return [];
    }

    if (isGrouped) {
      return getSelectedOptionFromGroups(fieldValue);
    }

    // Use optionsList instead of global options
    return getNestedSelect(optionsList, fieldValue);
  };

  return (
    <Styled.Container style={customStyle} margin={margin}>
      <Styled.SelectHeader fontSize={fontSize}>{header}</Styled.SelectHeader>
      <Controller
        control={control}
        name={name}
        rules={required}
        render={({ field: { onChange: onValueChange, value } }) => {
          const handleChange = createHandleChange(
            onValueChange,
            onChange,
            isMulti,
          );

          // Handle the case where grouped options are provided but form value is null
          // This can happen during component re-renders when options change
          const currentOptions = isGrouped ? groupedOptions : options;
          const getSelected = createGetSelected(value, isMulti, currentOptions);

          return (
            <Select
              ref={ref}
              // React select cannot add data-cy so you have to work with Id attribute
              id={cy}
              {...props}
              selectProps={{ defaultMenuIsOpen }}
              label={label}
              key={name}
              name={name}
              placeholder={placeholder}
              styles={selectStyle}
              value={getSelected()}
              isClearable={isClearable}
              allowSelectAll={allowSelectAll}
              isSearchable={isSearchable}
              isDisabled={isDisabled}
              isMulti={isMulti}
              closeMenuOnSelect={closeMenuOnSelect}
              controlShouldRenderValue={controlShouldRenderValue}
              hideSelectedOptions={hideSelectedOptions}
              filterOption={filterOption}
              createOptionPosition="first"
              isLoading={isLoading || formIsLoading}
              onChange={handleChange}
              onCreateOption={onCreateOption}
              menuPlacement={menuPlacement}
              onInputChange={onInputChange}
              maxMenuHeight={maxMenuHeight}
              isValidNewOption={() => true}
              formatCreateLabel={() => formatCreateLabel || `Add Company`}
              allowCreateWhileLoading
              menuShouldScrollIntoView={false}
              noOptionsMessage={() => noOptionsMessage ?? 'No options'}
              formatGroupLabel={isGrouped ? formatGroupLabel : undefined}
              components={{ Option: CustomSelectOption }}
              options={getOptions()}
            />
          );
        }}
      />
      <ValidationErrorMessage message={errorMessage} />
    </Styled.Container>
  );
});

CreatableSelect.defaultProps = {
  fontSize: '',
  customComponents: {},
  styles: {},
  isLoading: false,
  isDisabled: false,
  isSearchable: true,
  closeMenuOnSelect: true,
  header: '',
  validationLabel: undefined,
  menuPlacement: 'auto',
  required: {},
  options: [],
  label: '',
  className: '',
  isClearable: true,
  onChange: undefined,
  onInputChange: undefined,
  error: null,
  placeholder: 'Please Select',
  customStyle: {},
  isMulti: false,
  filterOption: undefined,
  cy: undefined,
  margin: undefined,
  controlShouldRenderValue: true,
  showCheckBox: false,
  hideSelectedOptions: false,
  allowSelectAll: false,
  maxMenuHeight: 'auto',
  icon: undefined,
  noOptionsMessage: undefined,
  defaultMenuIsOpen: false,
  formatCreateLabel: undefined,
  isGrouped: false,
  groupedOptions: [],
  control: undefined,
  formState: undefined,
  customGroupLabel: undefined,
};

CreatableSelect.propTypes = {
  fontSize: PropTypes.string,
  customComponents: PropTypes.shape({}),
  styles: PropTypes.shape({}),
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isSearchable: PropTypes.bool,
  closeMenuOnSelect: PropTypes.bool,
  validationLabel: PropTypes.string,
  menuPlacement: PropTypes.string,
  required: PropTypes.shape({}),
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  className: PropTypes.string,
  isClearable: PropTypes.bool,
  label: PropTypes.string,
  onChange: PropTypes.func,
  onInputChange: PropTypes.func,
  error: PropTypes.shape({}),
  placeholder: PropTypes.string,
  customStyle: PropTypes.shape({}),
  isMulti: PropTypes.bool,
  showCheckBox: PropTypes.bool,
  header: PropTypes.string,
  filterOption: PropTypes.func,
  cy: PropTypes.string,
  controlShouldRenderValue: PropTypes.bool,
  hideSelectedOptions: PropTypes.bool,
  allowSelectAll: PropTypes.bool,
  maxMenuHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  margin: PropTypes.string,
  icon: PropTypes.node,
  noOptionsMessage: PropTypes.string,
  defaultMenuIsOpen: PropTypes.bool,
  onCreateOption: PropTypes.func.isRequired,
  formatCreateLabel: PropTypes.string,
  isGrouped: PropTypes.bool,
  groupedOptions: PropTypes.arrayOf(PropTypes.shape({})),
  customGroupLabel: PropTypes.func,
  control: PropTypes.object,
  formState: PropTypes.object,
};

export default CreatableSelect;
